/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.selectedOptionIds = this.getSelectedOptionIds();
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.variantChangeRequestId = 0;

      this.addListeners();
      this.updateVariantContent();
      this.initMobileColorSwatchesCollapsible();
      if (!this.quickBuyContainer && !this.featuredProductContainer) this.applySearchParams();
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.boundHandleOptionPointerDown = this.handleOptionPointerDown.bind(this);
      this.querySelectorAll('.opt-label').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
        el.addEventListener('pointerdown', this.boundHandleOptionPointerDown, { passive: true });
      });
      // Dropdown options do not use .opt-label; prefetch on pointerdown as well.
      this.addEventListener('pointerdown', this.boundHandleOptionPointerDown, { passive: true });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.removeEventListener('pointerdown', this.boundHandleOptionPointerDown);
      this.querySelectorAll('.opt-label').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
        el.removeEventListener('pointerdown', this.boundHandleOptionPointerDown);
      });
      this.destroyMobileColorSwatchesCollapsible();
    }

    /**
     * Mobile-only: color option swatches default to one row with a trailing "+" to expand.
     */
    initMobileColorSwatchesCollapsible() {
      this.mobileColorSwatchEntries = [];
      const wraps = this.querySelectorAll('[data-mobile-color-swatches]');
      if (wraps.length === 0) return;

      this.mobileColorSwatchMql = window.matchMedia('(max-width: 767.98px)');
      this.boundMobileColorSwatchMqlChange = () => this.refreshAllMobileColorSwatches();
      this.mobileColorSwatchMql.addEventListener('change', this.boundMobileColorSwatchMqlChange);

      wraps.forEach((wrap) => {
        const expandBtn = wrap.querySelector('[data-expand-color-swatches]');
        const btns = wrap.querySelector('[data-color-swatches-btns]');
        if (!expandBtn || !btns) return;

        const onExpandClick = (e) => {
          e.preventDefault();
          const expanded = wrap.getAttribute('data-expanded') === 'true';
          if (expanded) {
            wrap.removeAttribute('data-expanded');
          } else {
            wrap.setAttribute('data-expanded', 'true');
          }
          this.refreshMobileColorSwatches(wrap);
        };
        expandBtn.addEventListener('click', onExpandClick);

        const ro = new ResizeObserver(() => this.refreshMobileColorSwatches(wrap));
        ro.observe(btns);

        this.mobileColorSwatchEntries.push({ ro, expandBtn, onExpandClick });
      });

      this.refreshAllMobileColorSwatches();
      requestAnimationFrame(() => this.refreshAllMobileColorSwatches());
    }

    destroyMobileColorSwatchesCollapsible() {
      this.mobileColorSwatchEntries?.forEach(({ ro, expandBtn, onExpandClick }) => {
        ro.disconnect();
        expandBtn.removeEventListener('click', onExpandClick);
      });
      this.mobileColorSwatchEntries = [];
      if (this.mobileColorSwatchMql && this.boundMobileColorSwatchMqlChange) {
        this.mobileColorSwatchMql.removeEventListener('change', this.boundMobileColorSwatchMqlChange);
      }
      this.mobileColorSwatchMql = null;
      this.boundMobileColorSwatchMqlChange = null;
    }

    refreshAllMobileColorSwatches() {
      this.querySelectorAll('[data-mobile-color-swatches]').forEach((wrap) => {
        this.refreshMobileColorSwatches(wrap);
      });
    }

    refreshMobileColorSwatches(wrap) {
      const mq = window.matchMedia('(max-width: 767.98px)');
      const btns = wrap.querySelector('[data-color-swatches-btns]');
      const expandBtn = wrap.querySelector('[data-expand-color-swatches]');
      if (!btns || !expandBtn) return;

      const plus = expandBtn.querySelector('.option-selector__swatches-expand__icon--plus');
      const minus = expandBtn.querySelector('.option-selector__swatches-expand__icon--minus');

      if (!mq.matches) {
        btns.style.removeProperty('max-height');
        btns.style.removeProperty('overflow');
        wrap.removeAttribute('data-expanded');
        wrap.dataset.overflow = 'false';
        expandBtn.hidden = true;
        expandBtn.setAttribute('aria-expanded', 'false');
        expandBtn.setAttribute('aria-label', expandBtn.dataset.ariaLabelExpand);
        minus?.classList.add('hidden');
        plus?.classList.remove('hidden');
        wrap.style.removeProperty('--color-swatch-collapsed-max-h');
        return;
      }

      const wasExpanded = wrap.getAttribute('data-expanded') === 'true';
      btns.style.maxHeight = 'none';
      btns.style.overflow = 'visible';
      wrap.removeAttribute('data-expanded');
      void btns.offsetHeight;

      const labels = [...btns.querySelectorAll('.opt-btn + .opt-label')].filter((label) => {
        const input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
        if (!input) return false;
        const inputStyle = window.getComputedStyle(input);
        if (inputStyle.display === 'none' || inputStyle.visibility === 'hidden') return false;
        const labelStyle = window.getComputedStyle(label);
        return labelStyle.display !== 'none';
      });

      if (labels.length === 0) {
        expandBtn.hidden = true;
        wrap.dataset.overflow = 'false';
        wrap.removeAttribute('data-expanded');
        expandBtn.setAttribute('aria-expanded', 'false');
        expandBtn.setAttribute('aria-label', expandBtn.dataset.ariaLabelExpand);
        minus?.classList.add('hidden');
        plus?.classList.remove('hidden');
        btns.style.removeProperty('max-height');
        btns.style.removeProperty('overflow');
        wrap.style.removeProperty('--color-swatch-collapsed-max-h');
        return;
      }

      // offsetTop is unreliable (inputs are position:absolute); measure vs btns box (matches overflow clip).
      const btnsRect = btns.getBoundingClientRect();
      const rects = labels.map((l) => l.getBoundingClientRect());
      const tops = rects.map((r) => r.top);
      const minTop = Math.min(...tops);
      const maxTop = Math.max(...tops);
      const ROW_EPS = 6;
      const hasOverflow = maxTop > minTop + ROW_EPS;

      let rowHeight = 0;
      if (hasOverflow) {
        const firstRowIdx = rects
          .map((r, i) => (Math.abs(r.top - minTop) < ROW_EPS ? i : -1))
          .filter((i) => i >= 0);
        const relTops = firstRowIdx.map((i) => rects[i].top - btnsRect.top);
        const relBottoms = firstRowIdx.map((i) => rects[i].bottom - btnsRect.top);
        const topEdge = Math.min(...relTops);
        const bottomEdge = Math.max(...relBottoms);
        // Extra px: subpixel, selected swatch box-shadow, line rounding
        rowHeight = Math.ceil(bottomEdge - topEdge) + 14;
        const firstLabel = labels[firstRowIdx[0]];
        if (firstLabel) {
          const cs = window.getComputedStyle(firstLabel);
          const mt = parseFloat(cs.marginTop) || 0;
          const mb = parseFloat(cs.marginBottom) || 0;
          const minRow = Math.ceil(firstLabel.offsetHeight + mt + mb);
          rowHeight = Math.max(rowHeight, minRow);
        }
      }

      if (!hasOverflow) {
        wrap.dataset.overflow = 'false';
        expandBtn.hidden = true;
        wrap.removeAttribute('data-expanded');
        expandBtn.setAttribute('aria-expanded', 'false');
        expandBtn.setAttribute('aria-label', expandBtn.dataset.ariaLabelExpand);
        minus?.classList.add('hidden');
        plus?.classList.remove('hidden');
        btns.style.removeProperty('max-height');
        btns.style.removeProperty('overflow');
        wrap.style.removeProperty('--color-swatch-collapsed-max-h');
        return;
      }

      wrap.dataset.overflow = 'true';
      expandBtn.hidden = false;
      wrap.style.setProperty('--color-swatch-collapsed-max-h', `${rowHeight}px`);

      if (wasExpanded) {
        wrap.setAttribute('data-expanded', 'true');
        expandBtn.setAttribute('aria-expanded', 'true');
        expandBtn.setAttribute('aria-label', expandBtn.dataset.ariaLabelCollapse);
        minus?.classList.remove('hidden');
        plus?.classList.add('hidden');
        btns.style.maxHeight = 'none';
        btns.style.overflow = 'visible';
      } else {
        wrap.removeAttribute('data-expanded');
        expandBtn.setAttribute('aria-expanded', 'false');
        expandBtn.setAttribute('aria-label', expandBtn.dataset.ariaLabelExpand);
        minus?.classList.add('hidden');
        plus?.classList.remove('hidden');
        btns.style.maxHeight = `${rowHeight}px`;
        btns.style.overflow = 'hidden';
      }
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.updateAvailability();
      this.refreshAllMobileColorSwatches();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Starts fetching variant HTML as soon as the user presses an option.
     * This overlaps network latency with the click → change event gap.
     * @param {PointerEvent} evt - Pointer event.
     */
    handleOptionPointerDown(evt) {
      const option = evt.target.closest('.opt-label, .custom-select__option.js-option');
      if (!option || !this.contains(option)) return;
      if (option.matches('.opt-btn:checked + .opt-label, .custom-select__option[aria-selected="true"]')) {
        return;
      }

      const url = this.getOptionPrefetchUrl(option);
      if (url) theme.fetchCache.preload(url, { priority: 'high' });
    }

    /**
     * Build a prefetch URL for a listed label or dropdown option.
     * @param {Element} optionEl - Option label or custom-select option.
     * @returns {?string}
     */
    getOptionPrefetchUrl(optionEl) {
      let input = null;
      if (optionEl.matches('.opt-label')) {
        input = document.getElementById(optionEl.htmlFor);
      } else if (optionEl.matches('.custom-select__option')) {
        input = optionEl;
      }
      if (!input) return null;

      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      if (inputOptionIndex < 0) return null;

      optionIds[inputOptionIndex] = input.dataset.valueId;
      if (optionIds.includes(null)) return null;

      return this.constructVariantUrl(
        input.dataset.productUrl,
        optionIds,
        input.dataset.variantId
      ).toString();
    }

    /**
     * Handles 'mousseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label')) return; // Already selected

      const url = this.getOptionPrefetchUrl(label);
      if (!url) return;

      // Short delay filters accidental hover without delaying real intent much.
      label.dataset.preloadTimeout = setTimeout(() => {
        label.dataset.preloaded = true;
        label.removeAttribute('data-preload-timeout');
        theme.fetchCache.preload(url);
      }, 60);
    }

    /**
     * Handles 'mousseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      url.searchParams.set('section_id', this.dataset.sectionId);
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      const variantId = evt.target.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, this.selectedOptionIds, variantId);
      const requestId = ++this.variantChangeRequestId;

      // Sync form id + browser URL immediately so Add to Cart does not use the
      // previous variant while the section HTML fetch is still in flight.
      this.syncSelectedVariantImmediately(variantId, combinedProductUrl);

      theme.fetchCache.fetch(url.toString(), { priority: 'high' })
        .then((responseText) => {
          // A newer selection has already been made. Do not let an older,
          // slower response overwrite the current variant and sticky ATC.
          if (requestId !== this.variantChangeRequestId) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.updateContent(this.section, html, combinedProductUrl);

          // Restore focus
          document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();

          // Update URL and announce changes on the replaced picker.
          const newVariantPicker = this.section.querySelector('variant-picker');
          newVariantPicker?.announceChange();
          newVariantPicker?.prefetchSiblingOptions();
        });
    }

    /**
     * Prefetch nearby option combinations while idle so the next switch is cached.
     */
    prefetchSiblingOptions() {
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 120));
      schedule(() => {
        if (!this.isConnected) return;

        const currentIds = this.getSelectedOptionIds();
        if (currentIds.includes(null)) return;

        const urls = new Set();
        this.optionSelectors.forEach((selector, optionIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not(.is-unavailable):not([aria-selected="true"]):not(:checked)'
          );
          options.forEach((option) => {
            if (urls.size >= 6) return;
            const optionIds = [...currentIds];
            optionIds[optionIndex] = option.dataset.valueId;
            if (optionIds.includes(null)) return;
            urls.add(this.constructVariantUrl(
              option.dataset.productUrl,
              optionIds,
              option.dataset.variantId
            ).toString());
          });
        });

        urls.forEach((url) => theme.fetchCache.preload(url));
      }, { timeout: 800 });
    }

    /**
     * Optimistically syncs the selected variant id into forms and the URL.
     * @param {string} [variantId] - Variant id from the changed option, if known.
     * @param {string} [combinedProductUrl] - Combined listing product URL, if any.
     */
    syncSelectedVariantImmediately(variantId, combinedProductUrl) {
      // Combined listings may switch products; wait for the fetched HTML.
      if (combinedProductUrl) return;
      if (this.selectedOptionIds.includes(null)) return;

      if (variantId) {
        this.syncProductFormVariantId(variantId);
        this.updateUrl(variantId);
        this.applyOptimisticVariantUi(variantId);
        return;
      }

      // Selection is complete but the new variant id is not on the option yet.
      // Clear stale ids so ATC cannot submit the previous variant.
      this.syncProductFormVariantId('');
    }

    /**
     * Looks up compact local variant data for optimistic UI updates.
     * @param {string} variantId - Variant id.
     * @returns {?object}
     */
    getVariantFromMap(variantId) {
      if (!variantId) return null;
      if (!this.variantMap) {
        const mapEl = this.querySelector('[data-variant-map]');
        this.variantMap = mapEl ? JSON.parse(mapEl.textContent) : {};
      }
      return this.variantMap[String(variantId)] || null;
    }

    /**
     * Updates price / ATC / media immediately from local variant data while
     * the full section HTML request is still in flight.
     * @param {string} variantId - Selected variant id.
     */
    applyOptimisticVariantUi(variantId) {
      const variant = this.getVariantFromMap(variantId);
      if (!variant) return;

      this.variant = {
        ...(this.variant || {}),
        id: variant.id,
        available: variant.available,
        featured_media: variant.featured_media
      };

      this.section.querySelectorAll('.price-container .price').forEach((priceEl) => {
        priceEl.classList.toggle('price--on-sale', Boolean(variant.on_sale));
        priceEl.classList.toggle('price--sold-out', !variant.available);

        const currentValue = priceEl.querySelector('.price__current .js-value');
        if (currentValue) currentValue.textContent = variant.price;

        const wasEl = priceEl.querySelector('.price__was');
        const wasValue = wasEl?.querySelector('.js-value');
        if (wasValue) wasValue.textContent = variant.compare_at_price || '';
        if (wasEl) wasEl.hidden = !variant.compare_at_price;

        const fromLabel = priceEl.querySelector('.price__from');
        if (fromLabel) fromLabel.hidden = true;
      });

      this.updateAddToCartButton();

      const stickyButton = this.section.querySelector('sticky-atc .add-to-cart[data-add-to-cart-text]');
      if (stickyButton) {
        stickyButton.textContent = variant.available
          ? stickyButton.dataset.addToCartText
          : (theme.strings.noStock || stickyButton.textContent);
      }

      if (variant.featured_media?.id) {
        const mediaGallery = this.section.querySelector('media-gallery');
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(variant.featured_media.id, true);
        }
      }
    }

    /**
     * Writes the variant id into product / sticky / instalments forms.
     * @param {string} variantId - Variant id to apply (may be empty).
     */
    syncProductFormVariantId(variantId) {
      this.section.querySelectorAll(
        '.js-product-form input[name="id"], .js-instalments-form input[name="id"]'
      ).forEach((input) => {
        if (input.value === variantId) return;
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      const stickyAtc = this.section.querySelector('sticky-atc');
      if (stickyAtc) {
        stickyAtc.currentVariantId = variantId;
        if (typeof stickyAtc.syncVariantId === 'function') {
          stickyAtc.syncVariantId(variantId);
        }
      }
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      if (!this.selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Ensure web component initialisation does not cause a visual jump
        const instalmentsHeight = toReplace.querySelector('shopify-payment-terms')?.clientHeight;
        if (instalmentsHeight) {
          const newTerms = replaceWith.querySelector('shopify-payment-terms');
          if (newTerms) newTerms.style.minHeight = `${instalmentsHeight}px`;
          clearTimeout(this.newTermsResetTimeoutId);
          this.newTermsResetTimeoutId = setTimeout(() => {
            replaceWith.querySelector('shopify-payment-terms').style.minHeight = null;
          }, 3000);
        }

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Sticky ATC markup is replaced for every variant response. Preserve
        // its current runtime visibility before insertion so the fresh element
        // does not animate in again from `sticky-atc--out` on every selection.
        if (toReplace.matches('sticky-atc') && replaceWith.matches('sticky-atc')) {
          replaceWith.classList.toggle(
            'sticky-atc--out',
            toReplace.classList.contains('sticky-atc--out')
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content - remember toReplace becomes detached
        toReplace.replaceWith(replaceWith);

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Dispatch change event on variant id inputs
        [
          ...(replaceWith.matches('input[name="id"]') ? [replaceWith] : []),
          ...replaceWith.querySelectorAll('input[name="id"]')
        ].forEach((input) => {
          if (input.closest('.js-product-form, .js-instalments-form')) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        this.selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'prune') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        this.addBtn.disabled = false;
        this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {string} [variantId] - Optional id to use before fetched variant JSON is available.
     */
    updateUrl(variantId) {
      if (this.dataset.updateUrl === 'false') return;
      const id = variantId || this.variant?.id;
      const url = id ? `${this.dataset.url}?variant=${id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.section.querySelector('variant-picker [type="application/json"]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
